/* Ashaki motion layer — subtle, purposeful animation.
   1. Smooth in-page anchor scrolling (with sticky-header offset)
   2. Soft landing when arriving on a #hash from another page
   3. Accordion (details) open/close animation
   4. Cards fade up as they enter the viewport
   5. Images fade in once loaded
   Everything respects prefers-reduced-motion and is skipped inside the theme editor. */
(function () {
  "use strict";

  var reduce = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var html = document.documentElement;
  html.classList.add("av1-motion");

  /* ------------------------------------------------------------------ */
  /* Scroll helpers                                                      */
  /* ------------------------------------------------------------------ */
  function headerOffset() {
    var h = document.querySelector(".av1-scope header");
    var off = 0;
    if (h) {
      var pos = getComputedStyle(h).position;
      if (pos === "sticky" || pos === "fixed") off = h.getBoundingClientRect().height;
    }
    return off + 16;
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  var rafId = null;
  function instantScroll(y) {
    var prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, y);
    html.style.scrollBehavior = prev;
  }

  function smoothScrollTo(targetY, done) {
    var startY = window.pageYOffset;
    var maxY = Math.max(0, html.scrollHeight - window.innerHeight);
    targetY = Math.max(0, Math.min(targetY, maxY));
    var dist = targetY - startY;
    if (reduce || Math.abs(dist) < 2) {
      instantScroll(targetY);
      if (done) done();
      return;
    }
    var duration = Math.min(1100, Math.max(450, Math.abs(dist) * 0.45));
    var start = null;
    if (rafId) cancelAnimationFrame(rafId);
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      instantScroll(startY + dist * easeInOutCubic(p));
      if (p < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        rafId = null;
        if (done) done();
      }
    }
    rafId = requestAnimationFrame(step);
  }

  /* Let the visitor take over at any time */
  ["wheel", "touchstart", "keydown"].forEach(function (ev) {
    window.addEventListener(ev, function () {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }, { passive: true });
  });

  function targetFor(hash) {
    if (!hash || hash === "#" || hash === "#!") return null;
    var id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (e) { return null; }
    if (!id) return null;
    return document.getElementById(id) || document.querySelector('[name="' + id.replace(/"/g, '\\"') + '"]');
  }

  function scrollToEl(el, newHash) {
    var y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    smoothScrollTo(y, function () {
      if (newHash) { try { history.pushState(null, "", newHash); } catch (e) {} }
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      try { el.focus({ preventScroll: true }); } catch (e) {}
    });
  }

  /* 1. In-page anchor clicks */
  document.addEventListener("click", function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest ? e.target.closest("a[href]") : null;
    if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    /* Same page = same path; ignore the query string so ?utm_… links still scroll in place */
    if (url.origin !== location.origin || url.pathname.replace(/\/$/, "") !== location.pathname.replace(/\/$/, "")) return;
    if (!url.hash) return;
    var el = targetFor(url.hash);
    if (!el) return;
    e.preventDefault();
    scrollToEl(el, location.pathname + location.search + url.hash);
  });

  /* Back / forward between hashes */
  window.addEventListener("hashchange", function () {
    var el = targetFor(location.hash);
    if (el) scrollToEl(el, null);
  });

  /* 2. Arriving on /#something from another page: the browser jumps so the
        target sits under the sticky header. Start a touch above and settle. */
  function landOnHash() {
    var el = targetFor(location.hash);
    if (!el) return;
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    var y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    if (reduce) { instantScroll(y); return; }
    instantScroll(Math.max(0, y - 140));
    setTimeout(function () { scrollToEl(el, null); }, 140);
  }
  if (location.hash && targetFor(location.hash)) {
    if (document.readyState === "complete") landOnHash();
    else window.addEventListener("load", landOnHash, { once: true });
  }

  /* ------------------------------------------------------------------ */
  /* 3. Accordions                                                       */
  /* ------------------------------------------------------------------ */
  if (!reduce && "animate" in Element.prototype) {
    document.querySelectorAll(".av1-scope details").forEach(function (d) {
      var summary = d.querySelector(":scope > summary");
      if (!summary) return;
      /* Group everything after the summary into one animatable panel */
      var panel = summary.nextElementSibling;
      if (!panel || panel.nextElementSibling || !panel.classList.contains("acc-panel")) {
        panel = document.createElement("div");
        panel.className = "acc-panel";
        while (summary.nextSibling) panel.appendChild(summary.nextSibling);
        d.appendChild(panel);
      }
      var busy = false;
      var easing = "cubic-bezier(0.4, 0, 0.2, 1)";
      summary.addEventListener("click", function (e) {
        e.preventDefault();
        if (busy) return;
        busy = true;
        if (d.open) {
          var h = panel.offsetHeight;
          panel.style.overflow = "hidden";
          var closing = panel.animate(
            [{ height: h + "px", opacity: 1 }, { height: "0px", opacity: 0 }],
            { duration: 260, easing: easing }
          );
          closing.onfinish = closing.oncancel = function () {
            d.open = false;
            panel.style.overflow = "";
            busy = false;
          };
        } else {
          d.open = true;
          var h2 = panel.offsetHeight;
          panel.style.overflow = "hidden";
          var opening = panel.animate(
            [{ height: "0px", opacity: 0 }, { height: h2 + "px", opacity: 1 }],
            { duration: 320, easing: easing }
          );
          opening.onfinish = opening.oncancel = function () {
            panel.style.overflow = "";
            busy = false;
          };
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 4. Reveal cards as they enter the viewport                          */
  /* ------------------------------------------------------------------ */
  if (!reduce && !designMode && "IntersectionObserver" in window) {
    var cards = document.querySelectorAll(
      /* homepage */ ".door, .plate, .plates-head, .craft-head, .triptych > *, .proof-inner, " +
      /* product page */ ".av1-scope .look-card, .av1-scope .review-card, .av1-scope .frame-tile, .av1-scope .why-inner"
    );
    var fold = window.innerHeight * 0.92;
    var pending = [];
    cards.forEach(function (el) {
      /* Only elements below the first screen — nothing already visible should blink */
      if (el.getBoundingClientRect().top > fold) {
        el.setAttribute("data-reveal", "");
        pending.push(el);
      }
    });
    if (pending.length) {
      var io = new IntersectionObserver(function (entries) {
        var batch = 0;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          var delay = Math.min(batch * 70, 280);
          batch += 1;
          setTimeout(function () { el.setAttribute("data-reveal", "in"); }, delay);
        });
      }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
      pending.forEach(function (el) { io.observe(el); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 5. Images fade in once they have loaded                             */
  /* ------------------------------------------------------------------ */
  if (!reduce) {
    document.querySelectorAll(".av1-scope .pgallery img, .av1-scope .look-card img, .door img, .plate img, .triptych img").forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      img.setAttribute("data-fade", "");
      var show = function () { img.setAttribute("data-fade", "in"); };
      img.addEventListener("load", show, { once: true });
      img.addEventListener("error", show, { once: true });
      /* Safety net: never leave an image invisible */
      setTimeout(show, 2500);
    });
  }
})();
